import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/psy-bloom-logo.png.asset.json";
import ancaAsset from "@/assets/anca-vlad-hero.jpeg.asset.json";
import { useRouteHead } from "@/hooks/use-route-head";

const ancaFaq = [
  { q: "Vin la psiholog ca să primesc răspunsuri clare?", a: "Terapia nu funcționează ca o listă de răspunsuri oferite din exterior. Este un proces în care înveți să înțelegi mai bine ce trăiești, ce nevoi ai și cum poți lua decizii mai apropiate de adevărul tău interior." },
  { q: "Dacă funcționez în fiecare zi, mai are sens să vin la terapie?", a: "Da. Faptul că îți faci responsabilitățile nu înseamnă că în interior nu poți fi epuizată, anxioasă sau deconectată de tine." },
  { q: "Lucrează Anca și cu anxietate sau atacuri de panică?", a: "Da. În lucrul cu anxietatea și atacurile de panică, accentul este pus pe înțelegerea mecanismelor care activează alarma interioară, pe reglarea sistemului nervos și pe reducerea fricii față de propriile reacții." },
  { q: "Pot veni dacă am dificultăți în relații?", a: "Da. Relațiile dificile, frica de abandon, nevoia de reasigurare, limitele neclare sau dependența de validarea celuilalt pot fi lucrate într-un cadru sigur." },
  { q: "Este terapia un proces rapid?", a: "Terapia nu promite schimbări rapide fără implicare. Este un proces de colaborare activă." },
  { q: "Ce se întâmplă dacă nu știu exact ce problemă am?", a: "Nu este nevoie să vii cu totul clarificat. O parte importantă a procesului este tocmai clarificarea." },
  { q: "Lucrează Anca și cu adolescenți?", a: "Da. Anca lucrează și cu adolescenți care se confruntă cu anxietate, presiune socială, dificultăți de integrare, conflicte familiale, suprastimulare sau întrebări legate de identitate." },
  { q: "Cât durează o ședință?", a: "Ședința are o durată de 50 de minute." },
  { q: "Cât costă ședința?", a: "Ședința are un cost între 300 și 450 lei. Poți afla mai multe apăsând butonul Programează o discuție unde poți discuta direct cu Anca." },
];

const routeHead = {
  meta: [
    { title: "Anca Vlad — Psiholog clinician | PSY BLOOM Institute" },
    {
      name: "description",
      content:
        "Anca Vlad, psiholog clinician la PSY BLOOM Institute. Sprijin pentru anxietate, suprasolicitare emoțională, overthinking și relații dificile.",
    },
    { property: "og:title", content: "Anca Vlad — Psiholog clinician" },
    {
      property: "og:description",
      content:
        "Când funcționezi în fiecare zi, dar în interior simți că nu mai ești tu. Terapie pentru claritate, autocunoaștere și reglare emoțională.",
    },
    { property: "og:image", content: ancaAsset.url },
    { property: "og:url", content: "https://psybloominstitute.ro/anca-vlad" },
  ],
  links: [{ rel: "canonical", href: "https://psybloominstitute.ro/anca-vlad" }],
  scripts: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Anca Vlad",
        jobTitle: "Psiholog clinician",
        image: ancaAsset.url,
        url: "https://psybloominstitute.ro/anca-vlad",
        worksFor: { "@type": "MedicalBusiness", name: "PSY BLOOM Institute" },
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: ancaFaq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    },
  ],
};

export const Route = createFileRoute("/anca-vlad")({
  component: AncaPage,
});

function AncaPage() {
  useRouteHead(routeHead);
  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <header className="w-full border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="PSY BLOOM Institute">
            <img src={logoAsset.url} alt="PSY BLOOM Institute" className="h-8 sm:h-9 w-auto" width={180} height={36} />
          </Link>
          <a
            href="https://wa.me/40747057753"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-primary px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[var(--shadow-card)] whitespace-nowrap"
          >
            Programează o discuție
          </a>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-surface to-background" aria-hidden="true" />
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-24">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <p className="text-sm uppercase tracking-wider text-[#32506c] font-medium font-sans mb-5">
                  Anca Vlad · Psiholog clinician
                </p>
                <h1 className="text-4xl md:text-5xl leading-[1.1] mb-6 text-balance">
                  Când funcționezi în fiecare zi, dar în interior simți că nu mai ești tu
                </h1>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
                  Anxietatea, suprasolicitarea emoțională, overthinking-ul și relațiile dificile pot ajunge să îți ocupe aproape tot spațiul interior, chiar dacă din exterior pari că te descurci.
                </p>
                <a
                  href="https://wa.me/40747057753"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[var(--shadow-card)]"
                >
                  Programează o discuție
                </a>
              </div>
              <div className="order-1 lg:order-2">
                <div className="rounded-3xl overflow-hidden border border-border shadow-[var(--shadow-card)] aspect-[4/5]">
                  <img
                    src={ancaAsset.url}
                    alt="Anca Vlad, psiholog clinician la PSY BLOOM Institute"
                    className="w-full h-full object-cover"
                    width={1000}
                    height={1250}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTRO SECTION */}
        <section className="py-20 md:py-28 bg-[#32506c]">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <p className="text-base md:text-xl text-white/90 leading-relaxed">
              Pentru persoanele care vor să înțeleagă ce se întâmplă în interiorul lor, nu doar să oprească temporar ceea ce simt. În terapie, emoțiile nu sunt tratate ca obstacole, ci ca semnale care pot fi înțelese, așezate și integrate într-o relație mai clară cu sinele.
            </p>
          </div>
        </section>

        {/* SECȚIUNEA 2 – CE SE ÎNTÂMPLĂ ÎN INTERIOR */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-4xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-6 text-balance">Poate nu este vizibil pentru ceilalți, dar tu simți că ceva s-a schimbat</h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Mulți oameni ajung în terapie după luni sau ani în care au încercat să se descurce singuri. Au citit, au ascultat podcasturi, au încercat să înțeleagă, au muncit mai mult, au controlat mai mult, au analizat mai mult, dar în interior starea de tensiune a rămas.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <BlockCard title="Funcționezi, dar pe pilot automat" text="Îți faci responsabilitățile, răspunzi oamenilor, mergi mai departe, dar în interior te simți obosită, deconectată și tot mai departe de tine." />
              <BlockCard title="Te consumi pentru lucruri care par mici" text="O conversație, un mesaj, o reacție sau o decizie pot declanșa ore întregi de analiză, vinovăție sau neliniște." />
              <BlockCard title="Simți că trebuie să controlezi tot" text="Încerci să anticipezi, să previi greșeli, să nu dezamăgești și să nu pierzi controlul, dar acest efort te epuizează." />
              <BlockCard title="Ai nevoie de validare, dar te judeci pentru asta" text="Poate cauți confirmări, reasigurări sau semne că ești suficientă, apoi te critici pentru că ai nevoie de ele." />
              <BlockCard title="Relațiile ajung să te consume emoțional" text="Frica de abandon, relațiile ambigue, dependența de reacțiile celuilalt sau dificultatea de a pune limite pot deveni surse constante de tensiune." />
              <BlockCard title="Nu mai știi exact cine ești" text="Între roluri, presiuni, responsabilități și așteptările celorlalți, poate deveni greu să mai distingi ce simți tu cu adevărat și ce faci doar ca să nu pierzi ceva sau pe cineva." />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA – EXPERTIZĂ */}
        <section className="relative pt-12 md:pt-16 pb-20 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-white" aria-hidden="true" />
          <div className="absolute inset-0 -z-10 opacity-[0.04]" aria-hidden="true" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #32506c 1px, transparent 1px), radial-gradient(circle at 70% 80%, #32506c 1px, transparent 1px)", backgroundSize: "70px 70px, 100px 100px" }} />
          <div className="mx-auto max-w-5xl px-6">
            <div className="text-center mb-14 md:mb-16">
              <h2 className="text-3xl md:text-5xl mb-5 text-balance text-[#32506c]">
                Pregătire profesională și acreditări
              </h2>
              <div className="h-px w-16 mx-auto bg-[#32506c]/30" />
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-start">
              <CprCard />
              <ExpertiseCard label="Atestări CPR" items={["Psihologie clinică (4805)", "Psihologie aplicată în domeniul securității naționale (1295)"]} />
              <ExpertiseCard label="Formări acreditate" items={["Psiho-oncologie (815)"]} />
              <ExpertiseCard label="Școli de psihoterapie" items={["Psihoterapie Ericksoniană în formare"]} />
              <div className="md:col-span-2">
                <ExpertiseCard label="Certificări suplimentare" items={["Specialist comunicare", "Formator", "Consilier pentru dezvoltare personală", "Specialist analiză și evaluare comportamentală", "Manager de proiect", "Expert informații pentru afaceri", "Pedagog social"]} />
              </div>
            </div>
          </div>
        </section>

        {/* SECȚIUNEA – APARIȚII MEDIA */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-[#32506c]/[0.09]">
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0f2438] via-[#1a3a55] to-[#32506c]" aria-hidden="true" />
          <div className="absolute inset-0 -z-10 opacity-[0.08]" aria-hidden="true" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)", backgroundSize: "60px 60px, 90px 90px" }} />
          <div className="mx-auto max-w-6xl px-6">
            <div className="text-center mb-14 md:mb-16">
              <h2 className="text-3xl md:text-5xl mb-5 max-w-2xl mx-auto text-[#32506c]">
                Anca în presă și televiziune
              </h2>
              <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto text-[#32506c]">
                Invitată în emisiuni și platforme media pentru a vorbi despre sănătate emoțională și echilibru interior.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-10 max-w-5xl mx-auto">
              <MediaCard videoId="nInVyBNz4ok" />
              <MediaCard videoId="v7--_c0RmKM" />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA 3 – TESTIMONIALE */}
        <section className="py-20 md:py-28 bg-[#32506c]">
          <div className="mx-auto max-w-6xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-4 text-white bg-[#32506c]">Ce spun oamenii care au lucrat cu Anca</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
              <TestimonialCard quote="„Lucrez cu Anca de aproape doi ani și pot spune că a fost una dintre cele mai valoroase experiențe din viața mea. Am pornit cu dorința de a mă cunoaște mai bine, iar pe parcurs am ajuns să înțeleg și să lucrez cu aspecte mult mai profunde din trecutul meu. Procesul mi-a adus claritate, creștere și o reconectare autentică cu mine însămi. Astăzi simt că mă înțeleg mai bine, iau decizii mai conștiente și am mai multă încredere în propriile resurse.”" author="Cristina" age="25 ani" />
              <TestimonialCard quote="„Am venit la Anca într-un moment în care mă simțeam complet pierdută și aveam impresia că nu mai văd nicio direcție clară în fața mea. În timp, am început să înțeleg altfel evenimentele dureroase prin care am trecut și să descopăr că ele pot deveni oportunități de autocunoaștere și creștere. Cel mai important lucru pe care l-am învățat în acest proces a fost să mă privesc cu mai multă blândețe și să accept ceea ce simt, fără să mă lupt permanent cu mine însămi.”" author="Luiza" age="24 ani" />
              <TestimonialCard quote="„Am venit la doamna psiholog Anca având multe întrebări și nelămuriri legate de mine și de relațiile din viața mea. Pe parcursul ședințelor, procesul de autocunoaștere m-a ajutat să mă înțeleg mai bine și să privesc diferit anumite situații. Am găsit un spațiu în care m-am simțit apreciată pentru ceea ce sunt și în care am putut vorbi deschis fără teama de a fi judecată. Relația cu părinții mei s-a îmbunătățit considerabil, iar conflictele dintre noi s-au redus mult. Pentru mine, experiența alături de Anca a fost una importantă și valoroasă pentru dezvoltarea mea personală.”" author="Alina" age="16 ani" />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA 4 – ABORDAREA */}
        <section className="pt-20 md:pt-28 pb-8 md:pb-12">
          <div className="mx-auto max-w-4xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-6 text-balance">Terapia nu înseamnă să primești o rețetă, ci să înveți să te înțelegi mai clar</h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Anca lucrează cu emoțiile în profunzime, fără să le transforme în dușmani și fără să le reducă la simptome izolate. Procesul terapeutic pornește de la ceea ce trăiești concret, de la întrebările care se repetă, de la reacțiile care par greu de controlat și de la tiparele care îți consumă energia.
              </p>
              <p className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#32506c]/10 text-sm font-medium text-[#32506c]">
                Durată ședință — 50 de minute
              </p>
            </div>
            <div className="space-y-4 mb-14">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center">
                Poate că ai încercat să înțelegi totul, să anticipezi fiecare problemă și să păstrezi controlul asupra lucrurilor, însă cu cât depui mai mult efort, cu atât oboseala interioară devine mai greu de ignorat
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              <PillarCard title="Claritate" text="Înțelegi mai bine ce se întâmplă în interiorul tău și de ce anumite reacții apar atât de intens." />
              <PillarCard title="Reglare emoțională" text="Înveți să rămâi în contact cu emoțiile fără să fii complet copleșită de ele." />
              <PillarCard title="Reconectare cu sinele" text="Începi să separi ce îți aparține cu adevărat de presiuni, frici, vinovăție și așteptările celorlalți." />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA – ADOLESCENȚĂ */}
        <section className="py-20 md:py-28 bg-[#f5f3ee]">
          <div className="mx-auto max-w-4xl px-6">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl mb-8 text-balance text-[#32506c]">
                Adolescența nu este doar o etapă dificilă, ci perioada în care începi să te întrebi cine ești.
              </h2>
              <div className="space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center text-balance">
                <p>
                  Pentru mulți adolescenți, dificultatea nu vine din lipsa performanței. Continuă să meargă la școală, își fac temele, ies cu prietenii și par să funcționeze normal. În interior însă, lucrurile pot arăta foarte diferit.
                </p>
                <p>
                  Apar întrebări despre identitate, valoare personală, apartenență și viitor. Apare presiunea de a fi suficient de bun, suficient de apreciat și suficient de acceptat. Iar atunci când aceste răspunsuri nu sunt clare, anxietatea, supragândirea și nesiguranța încep să ocupe tot mai mult spațiu.
                </p>
              </div>
            </div>
            <div className="space-y-5">
              <AdolescentQuote n="1" title="Anxietate și presiunea de a nu greși" text="Mulți adolescenți trăiesc cu senzația că trebuie să facă totul bine. Se tem să nu dezamăgească, să nu fie respinși sau să nu fie judecați de ceilalți. În timp, această presiune poate duce la anxietate, hipervigilență și epuizare emoțională." />
              <AdolescentQuote n="2" title="Overthinking și mintea care nu se oprește" text="Uneori problema nu este lipsa de informații, ci faptul că mintea analizează permanent fiecare situație. Conversații, mesaje, reacții ale colegilor, ale profesorilor sau ale părinților sunt întoarse pe toate părțile în încercarea de a evita greșelile și de a obține siguranță." />
              <AdolescentQuote n="3" title="Nevoia de validare și comparația constantă" text="Rețelele sociale și presiunea grupului fac comparația aproape imposibil de evitat. Mulți adolescenți ajung să își măsoare valoarea personală prin aprobarea celorlalți, prin rezultate sau prin imaginea pe care o proiectează în exterior." />
              <AdolescentQuote n="4" title="Dificultăți de integrare și anxietate socială" text="Sentimentul că nu te potrivești, că nu ești suficient de interesant sau că ceilalți te judecă poate transforma interacțiunile sociale în surse constante de stres. Chiar și atunci când sunt înconjurați de oameni, mulți adolescenți se simt singuri și neînțeleși." />
              <AdolescentQuote n="5" title="Confuzie legată de identitate și direcție" text="Una dintre cele mai frecvente întrebări în adolescență este: „Cine sunt eu, de fapt?”. Lipsa unui răspuns clar poate crea multă neliniște. Însă identitatea nu este ceva ce descoperi peste noapte. Este ceva ce construiești în timp, prin experiențe, alegeri și autocunoaștere." />
            </div>
            <p className="mt-12 text-base md:text-lg text-[#32506c] leading-relaxed text-center max-w-3xl mx-auto text-balance">
              Fiecare adolescent are propriul ritm. Uneori este nevoie doar de un spațiu sigur în care să poată pune întrebări, să înțeleagă ce trăiește și să descopere că nu este nimic defect la el. Există o logică în ceea ce simte, iar această logică poate fi înțeleasă.
            </p>
          </div>
        </section>

        {/* SECȚIUNEA 5 – FAQ */}
        <section className="py-20 md:py-28 bg-[#dbe1e8]">
          <div className="mx-auto max-w-3xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-4 text-[#32506c]">Întrebări frecvente</h2>
            </div>
            <div className="space-y-4">
              <FaqItem question="Vin la psiholog ca să primesc răspunsuri clare?" answer="Terapia nu funcționează ca o listă de răspunsuri oferite din exterior. Este un proces în care înveți să înțelegi mai bine ce trăiești, ce nevoi ai și cum poți lua decizii mai apropiate de adevărul tău interior." />
              <FaqItem question="Dacă funcționez în fiecare zi, mai are sens să vin la terapie?" answer="Da. Faptul că îți faci responsabilitățile nu înseamnă că în interior nu poți fi epuizată, anxioasă sau deconectată de tine. Mulți oameni cer ajutor tocmai pentru că în exterior par bine, dar în interior simt că nu mai pot continua la fel." />
              <FaqItem question="Lucrează Anca și cu anxietate sau atacuri de panică?" answer="Da. În lucrul cu anxietatea și atacurile de panică, accentul este pus pe înțelegerea mecanismelor care activează alarma interioară, pe reglarea sistemului nervos și pe reducerea fricii față de propriile reacții." />
              <FaqItem question="Pot veni dacă am dificultăți în relații?" answer="Da. Relațiile dificile, frica de abandon, nevoia de reasigurare, limitele neclare sau dependența de validarea celuilalt pot fi lucrate într-un cadru sigur, cu atenție la tiparele care se repetă." />
              <FaqItem question="Este terapia un proces rapid?" answer="Terapia nu promite schimbări rapide fără implicare. Este un proces de colaborare activă, în care psihologul oferă spațiul, ghidajul și structura, iar tu participi prin sinceritate, curiozitate și disponibilitatea de a lucra cu tine." />
              <FaqItem question="Ce se întâmplă dacă nu știu exact ce problemă am?" answer="Nu este nevoie să vii cu totul clarificat. De multe ori, oamenii ajung în terapie cu întrebări, confuzie, oboseală sau sentimentul că ceva nu mai este în regulă. O parte importantă a procesului este tocmai clarificarea." />
              <FaqItem question="Lucrează Anca și cu adolescenți?" answer="Da. Anca lucrează și cu adolescenți care se confruntă cu anxietate, presiune socială, dificultăți de integrare, conflicte familiale, suprastimulare sau întrebări legate de identitate și relația cu ceilalți." />
              <FaqItem question="Cât durează o ședință?" answer="Ședința are o durată de 50 de minute." />
              <FaqItem question="Cât costă ședința?" answer="Ședința are un cost între 300 și 450 lei. Poți afla mai multe apăsând butonul Programează o discuție unde poți discuta direct cu Anca." />
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-[#32506c]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="Navigare subsol" className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/80 mb-8">
            <Link to="/" hash="despre" className="hover:text-white transition-colors">Despre</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
            <Link to="/politica-de-confidentialitate" className="hover:text-white transition-colors">Politica de confidentialitate</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Politica cookies</Link>
            <Link to="/termeni-si-conditii" className="hover:text-white transition-colors">Termeni si conditii</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/20 text-xs text-white/60">
            <p>© {new Date().getFullYear()} PSY BLOOM Institute</p>
            <p>
              Proiect creat de{" "}
              <a href="https://algoritmii.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Algoritmii.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BlockCard({ title, text }: { title: string; text: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 md:p-8 border border-border/60 shadow-[var(--shadow-card)]">
      <h3 className="text-lg md:text-xl mb-3">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function TestimonialCard({ quote, author, age }: { quote: string; author: string; age: string }) {
  const initial = author.charAt(0).toUpperCase();
  return (
    <figure className="group relative rounded-2xl p-[1px] bg-gradient-to-br from-white/40 via-white/10 to-transparent shadow-[0_25px_60px_-25px_rgba(0,0,0,0.5)] transition-all hover:-translate-y-1 hover:shadow-[0_35px_80px_-25px_rgba(0,0,0,0.6)]">
      <div className="relative h-full bg-gradient-to-br from-white to-[#f5f3ee] rounded-2xl p-7 md:p-9 flex flex-col overflow-hidden">
        <div className="relative flex items-center gap-2 mb-5">
          <span className="h-px w-8 bg-[#32506c]/40" aria-hidden="true" />
          <svg className="w-5 h-5 text-[#32506c]" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2V8zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2V8z"/>
          </svg>
        </div>
        <blockquote className="relative text-base leading-relaxed text-foreground/90 italic mb-7 flex-1">{quote}</blockquote>
        <figcaption className="relative flex items-center gap-3 pt-5 border-t border-[#32506c]/15">
          <span aria-hidden="true" className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#32506c] to-[#1e3a55] text-white text-sm font-medium shadow-md">{initial}</span>
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground block leading-tight">{author}</span>
            <span className="text-xs">{age}</span>
          </span>
        </figcaption>
      </div>
    </figure>
  );
}

function MediaCard({ videoId }: { videoId: string }) {
  return (
    <div className="group relative">
      <div className="absolute -inset-1 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-3xl blur-md opacity-60 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
      <div className="relative rounded-2xl overflow-hidden border border-white/20 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] bg-black aspect-video">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title="Apariție media Anca Vlad"
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    </div>
  );
}

function PillarCard({ title, text }: { title: string; text: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 md:p-8 border border-border/60 shadow-[var(--shadow-card)] text-center">
      <h3 className="text-lg md:text-xl mb-3">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="bg-card rounded-2xl border border-border/60 shadow-[var(--shadow-card)] group">
      <summary className="flex items-center justify-between cursor-pointer p-5 md:p-6 text-left select-none">
        <span className="font-medium text-[#32506c] pr-4">{question}</span>
        <span aria-hidden="true" className="text-[#32506c] transition-transform group-open:rotate-180 shrink-0 ml-2">▾</span>
      </summary>
      <div className="px-5 md:px-6 pb-5 md:pb-6 text-[#32506c] leading-relaxed">
        {answer}
      </div>
    </details>
  );
}

function CprCard() {
  return (
    <div className="relative bg-gradient-to-br from-[#32506c] to-[#1e3a55] rounded-2xl p-7 md:p-8 text-white shadow-[0_20px_60px_-20px_rgba(50,80,108,0.5)] overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" aria-hidden="true" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[10px] uppercase tracking-[0.2em] font-medium mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
          Acreditare oficială
        </div>
        <p className="text-white/70 text-sm mb-2">Cod Colegiul Psihologilor din România</p>
        <p className="text-3xl md:text-4xl font-semibold tracking-tight">33741</p>
      </div>
    </div>
  );
}

function ExpertiseCard({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="relative bg-white rounded-2xl p-7 md:p-8 border border-[#32506c]/15 shadow-[0_10px_40px_-15px_rgba(50,80,108,0.25)]">
      <div className="absolute top-0 left-7 -translate-y-1/2 px-3 py-1 rounded-full bg-[#32506c] text-[10px] uppercase tracking-[0.2em] text-white font-medium">
        {label}
      </div>
      <ul className="mt-3 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-[#32506c] leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#32506c]/60 shrink-0" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AdolescentQuote({ n, title, text }: { n: string; title: string; text: string }) {
  return (
    <blockquote className="relative bg-white rounded-2xl p-6 md:p-8 border-l-4 border-[#32506c] shadow-[var(--shadow-card)]">
      <div className="flex items-start gap-5">
        <span className="text-3xl md:text-4xl font-serif text-[#32506c]/40 leading-none shrink-0">{n}</span>
        <div>
          <h3 className="text-lg md:text-xl mb-2 text-[#32506c]">{title}</h3>
          <p className="text-muted-foreground leading-relaxed">{text}</p>
        </div>
      </div>
    </blockquote>
  );
}
